import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Search, 
  ArrowLeft, 
  BookOpen,
  Loader2,
  X,
  Filter,
  Bookmark,
  BookmarkCheck
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/ai-granth`;

// Category filters
const CATEGORIES = [
  { id: 'all', name: 'All', icon: '📚' },
  { id: 'gita', name: 'Gita', icon: '🙏' },
  { id: 'upanishad', name: 'Upanishads', icon: '📿' },
  { id: 'stotra', name: 'Stotras', icon: '🕉️' },
  { id: 'chalisa', name: 'Chalisa', icon: '🎵' },
  { id: 'pooja_path', name: 'Mantras', icon: '🪔' }
];

export default function AIGranthSearchPage({ user }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [bookmarkedVerses, setBookmarkedVerses] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setQuery(q);
      performSearch(q);
    }
    if (user) {
      fetchBookmarks();
    }
  }, [searchParams, user]);

  const fetchBookmarks = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/bookmarks?token=${token}`);
      const ids = new Set(response.data.bookmarks?.map(b => b.bookmark.verse_id) || []);
      setBookmarkedVerses(ids);
    } catch (error) {
      console.error('Failed to fetch bookmarks:', error);
    }
  };

  const performSearch = async (q) => {
    if (!q || q.length < 2) return;

    setLoading(true);
    setSearched(true);
    try {
      let url = `${API}/search?q=${encodeURIComponent(q)}&limit=50`;
      if (selectedCategory !== 'all') {
        url += `&category=${selectedCategory}`;
      }
      const response = await axios.get(url);
      setResults(response.data.results || []);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (query.trim() && query.length >= 2) {
      navigate(`/ai-granth/search?q=${encodeURIComponent(query)}`);
    }
  };

  const handleCategoryChange = (catId) => {
    setSelectedCategory(catId);
    if (query.trim() && query.length >= 2) {
      performSearch(query);
    }
  };

  const handleBookmark = async (verse) => {
    if (!user) {
      toast.error('Please login to bookmark verses');
      return;
    }

    const token = localStorage.getItem('dharmbook_token');
    const isBookmarked = bookmarkedVerses.has(verse.id);

    try {
      if (isBookmarked) {
        await axios.delete(`${API}/bookmarks/${verse.id}?token=${token}`);
        setBookmarkedVerses(prev => {
          const newSet = new Set(prev);
          newSet.delete(verse.id);
          return newSet;
        });
        toast.success('Bookmark removed');
      } else {
        await axios.post(
          `${API}/bookmarks?book_id=${verse.book_id}&chapter_id=${verse.chapter_id}&verse_id=${verse.id}&token=${token}`
        );
        setBookmarkedVerses(prev => new Set(prev).add(verse.id));
        toast.success('Verse bookmarked');
      }
    } catch (error) {
      toast.error('Failed to update bookmark');
    }
  };

  const highlightMatch = (text, query) => {
    if (!text || !query) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) => 
      regex.test(part) ? (
        <mark key={i} className="bg-amber-200 text-amber-900 rounded px-0.5">{part}</mark>
      ) : part
    );
  };

  return (
    <div className="min-h-screen bg-[#FDF8F3]">
      {/* Header */}
      <div className="bg-white border-b border-amber-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/ai-granth')}
              className="p-2 hover:bg-amber-50 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5 text-amber-800" />
            </button>

            <div className="flex-1 relative">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search within scriptures..."
                className="w-full pl-10 pr-10 py-3 bg-amber-50 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500"
                data-testid="search-input"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-500" />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-amber-200 rounded-full"
                >
                  <X className="w-4 h-4 text-amber-600" />
                </button>
              )}
            </div>

            <button
              onClick={handleSearch}
              className="px-4 py-3 bg-amber-600 text-white font-medium rounded-xl hover:bg-amber-700"
              data-testid="search-btn"
            >
              Search
            </button>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-3 rounded-xl border ${showFilters ? 'bg-amber-100 border-amber-300' : 'bg-white border-amber-200'}`}
            >
              <Filter className="w-5 h-5 text-amber-700" />
            </button>
          </div>

          {/* Category Filters */}
          {showFilters && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-amber-100">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1 transition-all ${
                    selectedCategory === cat.id
                      ? 'bg-amber-600 text-white'
                      : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                  }`}
                >
                  <span>{cat.icon}</span>
                  <span>{cat.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
          </div>
        ) : searched ? (
          <>
            <p className="text-sm text-amber-700 mb-6">
              {results.length} {results.length === 1 ? 'result' : 'results'} found for "{searchParams.get('q')}"
            </p>

            {results.length === 0 ? (
              <div className="text-center py-16">
                <BookOpen className="w-16 h-16 text-amber-300 mx-auto mb-4" />
                <p className="text-amber-700 mb-2">No verses found</p>
                <p className="text-sm text-amber-500">Try different keywords or check spelling</p>
              </div>
            ) : (
              <div className="space-y-4">
                {results.map((result, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl p-5 border border-amber-100 hover:shadow-md hover:border-amber-200 transition-all"
                  >
                    {/* Header with Source & Bookmark */}
                    <div className="flex items-start justify-between mb-3">
                      <Link
                        to={`/ai-granth/book/${result.book?.id || result.verse?.book_id}/chapter/${result.chapter?.id || result.verse?.chapter_id}`}
                        className="flex items-center gap-2 text-sm text-amber-600 hover:text-amber-800"
                      >
                        <BookOpen className="w-4 h-4" />
                        <span>{result.book?.title}</span>
                        <span>•</span>
                        <span>Ch. {result.chapter?.chapter_number}</span>
                        <span>•</span>
                        <span>V. {result.verse?.verse_number}</span>
                      </Link>
                      
                      {user && (
                        <button
                          onClick={() => handleBookmark(result.verse)}
                          className="p-1.5 hover:bg-amber-100 rounded-lg transition-colors"
                          data-testid={`bookmark-${result.verse?.id}`}
                        >
                          {bookmarkedVerses.has(result.verse?.id) ? (
                            <BookmarkCheck className="w-5 h-5 text-amber-600" />
                          ) : (
                            <Bookmark className="w-5 h-5 text-amber-400" />
                          )}
                        </button>
                      )}
                    </div>

                    {/* Original Text */}
                    <Link to={`/ai-granth/book/${result.book?.id || result.verse?.book_id}/chapter/${result.chapter?.id || result.verse?.chapter_id}`}>
                      <p 
                        className="text-amber-900 font-medium mb-2 hover:text-amber-700"
                        style={{ fontFamily: 'Noto Serif Devanagari, serif' }}
                      >
                        {highlightMatch(result.verse?.original_text?.split('\n')[0], searchParams.get('q'))}
                      </p>

                      {/* Meaning */}
                      {(result.verse?.english_meaning || result.verse?.hindi_meaning) && (
                        <p className="text-sm text-amber-700 line-clamp-2">
                          {highlightMatch(
                            result.verse?.english_meaning || result.verse?.hindi_meaning,
                            searchParams.get('q')
                          )}
                        </p>
                      )}
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <Search className="w-16 h-16 text-amber-300 mx-auto mb-4" />
            <p className="text-amber-700 mb-2">Search within sacred scriptures</p>
            <p className="text-sm text-amber-500">Enter at least 2 characters to search</p>
          </div>
        )}
      </div>
    </div>
  );
}
